import { useCallback, useEffect, useState } from 'react';
import { CURRENT_ENTITY_ID } from 'hooks/keys';
import { generateStorageKey, useStorage } from 'hooks/useStorage';
import useGetUser from 'hooks/useGetUser';

export const CURRENT_ENTITY_ID_CHANGED_EVENT = 'todai-app.current-entity-id-changed';

type CurrentEntityIdChangedEventDetail = {
    userId: string;
    entityId: string | null;
};

export const notifyCurrentEntityIdChanged = (userId: string, entityId: string | null) => {
    window.dispatchEvent(new CustomEvent<CurrentEntityIdChangedEventDetail>(
        CURRENT_ENTITY_ID_CHANGED_EVENT,
        { detail: { userId, entityId } },
    ));
};

const useGetCurrentEntityId = () => {
    const user = useGetUser();
    const { specifiedGetItem } = useStorage('local', user?.id ?? '');
    const userId = user?.id ?? '';
    const getCurrentEntityId = useCallback(
        () => specifiedGetItem<string | null>(CURRENT_ENTITY_ID, null),
        [specifiedGetItem],
    );
    const [currentEntityId, setCurrentEntityId] = useState<string | null>(getCurrentEntityId);

    useEffect(() => {
        setCurrentEntityId(getCurrentEntityId());
    }, [getCurrentEntityId]);

    useEffect(() => {
        const onCurrentEntityIdChanged = (event: Event) => {
            const { detail } = event as CustomEvent<CurrentEntityIdChangedEventDetail>;

            if (detail.userId !== userId) {
                return;
            }

            setCurrentEntityId(detail.entityId);
        };

        const onStorageChanged = (event: StorageEvent) => {
            if (event.key !== generateStorageKey(userId, CURRENT_ENTITY_ID)) {
                return;
            }

            setCurrentEntityId(getCurrentEntityId());
        };

        window.addEventListener(CURRENT_ENTITY_ID_CHANGED_EVENT, onCurrentEntityIdChanged);
        window.addEventListener('storage', onStorageChanged);

        return () => {
            window.removeEventListener(CURRENT_ENTITY_ID_CHANGED_EVENT, onCurrentEntityIdChanged);
            window.removeEventListener('storage', onStorageChanged);
        };
    }, [getCurrentEntityId, userId]);

    return currentEntityId;
};

export default useGetCurrentEntityId;
